import React, { useEffect, useMemo, useState } from "react";
import images from "../../assets/images";
import { AgGridReact } from "ag-grid-react";
import { themeQuartz } from "ag-grid-community";

import Modal from "../../components/modal/Modal";
import {
  useDepartmentStore,
  useOrgUsersStore,
  usePremisesStore,
  useRegionStore,
  useRoleManageStore,
} from "../../store/store";
import { MultiSelectDropdown } from "../../components/dropdown/MultiSelectDropdown";
import { Dropdown } from "../../components/dropdown/Dropdown";

const gridTheme = themeQuartz.withParams({
  backgroundColor: "#fff",
  foregroundColor: "#2a2a2a",
  headerTextColor: "#2a2a2a",
  headerBackgroundColor: "#E9F5FF",
  oddRowBackgroundColor: "rgb(0, 0, 0, 0.03)",
  headerColumnResizeHandleColor: "rgb(126, 46, 132)",
  fontSize: "0.75rem",
  headerFontWeight: "bold",
});

type userFormData = {
  employeeId: string;
  firstName: string;
  lastName: string;
  email: string;
  mobile: string;
  department: string;
  supervisor: string;
  role: string;
  password: string;
  region: string[];
  premise: string[];
  // userType: string;
};

const emptyForm: userFormData = {
  employeeId: "",
  firstName: "",
  lastName: "",
  email: "",
  mobile: "",
  department: "",
  supervisor: "",
  role: "",
  password: "",
  region: [],
  premise: [],
  // userType: "",
};

const validUserTypes = [
  { value: "orgmanager", label: "Manager" },
  { value: "orguser", label: "User" },
];

const UserManagement: React.FC = () => {
  const [rowData, setRowData] = useState<userFormData[]>([]);
  const [originalData, setOriginalData] = useState<userFormData[]>([]);

  const [isModalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");

  const [selectedUser, setSelectedUser] = useState(null);
  const [form, setForm] = React.useState<userFormData>(emptyForm);

  // ── Filter state ─────────────────────────────────────────────────────────
  const [filterRegion, setFilterRegion] = useState<string>("");
  const [filterPremise, setFilterPremise] = useState<string>("");
  const [errors, setErrors] = useState<
    Partial<Record<keyof userFormData, string>>
  >({});

  const {
    fetchOrgUsers,
    fetchAdminUsers,
    createOrgUser,
    updateOrgUser,
    orgUsersData,
    loading,
    error,
    setError,
  } = useOrgUsersStore();
  const { rolesData, fetchAllRoles } = useRoleManageStore();

  const { departments, fetchAllDepartments } = useDepartmentStore();
  const { allPremises, fetchPremises } = usePremisesStore();
  const { regions, fetchRegions } = useRegionStore();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    // if (name === "region") {
    //   setSelectedRegion(value);
    //   setForm((prev) => ({ ...prev, premise: "" }));
    // }
    setForm({ ...form, [name]: value });
    setErrors({ ...errors, [name]: "" });
  };

  const isView = modalMode === "view";
  const isEdit = modalMode === "edit";
  const title = {
    create: "Create User",
    edit: "Edit User",
    view: "View User",
  }[modalMode];

  const handleCreate = () => {
    setModalMode("create");
    // setForm(emptyForm);
    // setSelectedUser(null);
    setModalOpen(true);
    setError(null);
    setErrors({});
  };

  const handleView = (user: any) => {
    setModalMode("view");
    setSelectedUser(user);
    setForm({ ...user });
    setModalOpen(true);
    setError(null);
    setErrors({});
  };

  const handleEdit = (user: any) => {
    const editableUser = {
      employeeId: user.employeeId || "",
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      email: user.email || "",
      mobile: user.mobile || "",
      department: user.department || "",
      supervisor: user.supervisor || "",
      role: user.role || "",
      password: user.password || "",
      region: user.region || "",
      premise: user.premise || "",
      // userType: user?.roles[0],
    };

    setForm(editableUser);
    setModalMode("edit");
    setSelectedUser(user);
    // setForm({ ...user });
    setModalOpen(true);
    setError(null);
    setErrors({});
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    Object.entries(form).forEach(([key, value]) => {
      if (key === "password" && isEdit) return;
      if (
        (typeof value === "string" && value.trim() === "") ||
        value === "" ||
        value === null ||
        value === undefined
      ) {
        newErrors[key] = `${key} is required`;
      }
    });

    if (
      form.email &&
      !/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(form.email)
    ) {
      newErrors.email = "Invalid email format";
    }

    if (form.mobile && !/^\d{10}$/.test(form.mobile)) {
      newErrors.mobile = "Mobile must be exactly 10 digits";
    }

    if (form.password.length < 6 && !isEdit) {
      newErrors.password = "Password must be at least 6 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleModalSubmit = async (data: any) => {
    if (!validateForm()) return;

    if (modalMode === "create") {
      try {
        const successs = await createOrgUser(data);
        if (successs) {
          setSelectedUser(null);
          setForm(emptyForm);
          setModalOpen(false);
        }
      } catch (e: any) {
        console.error("Create failed", e);
      }
    } else if (modalMode === "edit") {
      const payload = {
        employeeId: data.employeeId,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        mobile: data.mobile,
        department: data.department,
        supervisor: data.supervisor,
        role: data.role,
        region: data.region,
        premise: data.premise,
        // userType: data.userType,
        ...(data.password && { password: data.password }),
      };
      try {
        const successs = await updateOrgUser(
          (selectedUser as any)?._id,
          payload,
        );
        if (successs) {
          setSelectedUser(null);
          setForm(emptyForm);
          setModalOpen(false);
        }
      } catch (e: any) {
        console.error("Update failed", e);
        setForm(emptyForm);
      }
    }
  };

  const searchData = async (keyword: any) => {
    if (!keyword.trim()) {
      setRowData(originalData);
      return;
    }

    const filteredData = originalData.filter((row) =>
      Object.values(row).some((value) =>
        String(value)?.toLowerCase().includes(keyword.toLowerCase()),
      ),
    );
    setRowData(filteredData);
  };

  // ── Derive filtered rows from active filters ──────────────────────────────
  const filteredRowData = useMemo(() => {
    let data = rowData;

    if (filterRegion) {
      data = data.filter((user: any) => {
        const userRegions: string[] = Array.isArray(user.region)
          ? user.region
          : user.region
            ? [user.region]
            : [];
        return userRegions.includes(filterRegion);
      });
    }

    if (filterPremise) {
      data = data.filter((user: any) => {
        const userPremises: string[] = Array.isArray(user.premise)
          ? user.premise
          : user.premise
            ? [user.premise]
            : [];
        return userPremises.includes(filterPremise);
      });
    }

    return data;
  }, [rowData, filterRegion, filterPremise]);

  useEffect(() => {
    setIsLoading(true);
    const isLoaded =
      orgUsersData?.length && departments?.length && rolesData?.length;
    // &&
    // allPremises.length &&
    // regions.length;
    if (!isLoaded) return;

    const userData = orgUsersData.map((user: any) => {
      const department = departments.find(
        (d: any) => d._id === user.department,
      );
      const role = rolesData.find((r) => r._id === user.role);
      // const region = regions.find((r) => r._id === user.region);
      // const premise = allPremises.find((prem) => prem._id === user.premise);

      return {
        ...user,
        departmentName: department?.departmentName || "N/A",
        roleName: role?.roleName || "N/A",
        // region: region?.regionName || "N/A",
        // premise: premise?.premisesName || "N/A",
      };
    });

    setRowData(userData);
    setOriginalData(userData);
    setIsLoading(false);
  }, [orgUsersData, departments, rolesData]);

  useEffect(() => {
    if (fetchAdminUsers) {
      fetchAdminUsers();
    } else {
      fetchOrgUsers();
    }
    if (!rolesData.length) fetchAllRoles();
    if (!departments.length) fetchAllDepartments();
    if (!allPremises.length) fetchPremises();
    if (!regions.length) fetchRegions();
  }, []);

  const [columnDefs, setColDefs] = useState<any>([
    {
      field: "name",
      headerName: "Name",
      sortable: false,
      minWidth: 30,
      maxWidth: 220,
      flex: 0,
      headerStyle: {
        backgroundColor: "#e9f5ff",
      },
      filter: false,
      cellStyle: {
        textAlign: "left",
        padding: "10px 0 0 15px",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      },
      valueGetter: (params: any) => {
        const fullName = `${params.data?.firstName || ""} ${
          params.data?.lastName || ""
        }`.trim();
        return params.data?.employeeId
          ? `${fullName} (${params.data.employeeId})`
          : fullName;
      },
      tooltipValueGetter: (params: any) => {
        const fullName = `${params.data?.firstName || ""} ${
          params.data?.lastName || ""
        }`.trim();
        return params.data?.employeeId
          ? `${fullName} (${params.data.employeeId})`
          : fullName;
      },
    },
    {
      field: "departmentName",
      headerName: "Department",
      sortable: false,
      // minWidth: 20,
      // flex: 1,
      minWidth: 30,
      maxWidth: 160,
      flex: 0,
      headerStyle: { backgroundColor: "#e9f5ff" },
      filter: false,
      cellStyle: {
        padding: "10px 0 0 10px",
        textAlign: "left",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      },
      tooltipField: "departmentName",

      // valueGetter: (params: any) => {
      //   const department = departments?.find(
      //     (r: any) => r._id === params.data.department
      //   );
      //   return department ? department.departmentName : "Loading...";
      // },
      // tooltipValueGetter: (params: any) => {
      //   const department = departments?.find(
      //     (r: any) => r._id === params.data.department
      //   );
      //   return department ? department.departmentName : "Loading...";
      // },
    },
    {
      field: "supervisor",
      headerName: "Supervisor",
      sortable: false,
      // minWidth: 20,
      // flex: 1,
      minWidth: 30,
      maxWidth: 120,
      flex: 0,
      headerStyle: { backgroundColor: "#e9f5ff" },
      filter: false,
      cellStyle: {
        padding: "10px 0 0 10px",
        textAlign: "left",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      },
      tooltipField: "supervisor",
    },
    {
      field: "email",
      headerName: "Email",
      sortable: false,
      // minWidth: 20,
      // flex: 0,
      minWidth: 120,
      // maxWidth: 160,
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      filter: false,
      cellStyle: {
        padding: "10px 0 0 10px",
        textAlign: "left",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      },
      tooltipField: "email",
    },
    {
      field: "roleName",
      headerName: "Role",
      sortable: false,
      // minWidth: 20,
      flex: 1,
      minWidth: 30,
      // maxWidth: 120,
      // flex: 0,
      headerStyle: { backgroundColor: "#e9f5ff" },
      filter: false,
      cellStyle: {
        padding: "10px 0 0 10px",
        textAlign: "left",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      },
      tooltipField: "rowName",
      // valueGetter: (params: any) => {
      //   const role = rolesData?.find((r) => r._id === params.data.role);
      //   return role ? role.roleName : "Loading...";
      // },
      // tooltipValueGetter: (params: any) => {
      //   const role = rolesData?.find((r) => r._id === params.data.role);
      //   return role ? role.roleName : "Loading...";
      // },
    },
    {
      field: "action",
      headerName: "Action",
      cellStyle: { padding: "10px 10px 0 5px ", textAlign: "right" },
      // minWidth: 20,
      // flex: 1,
      minWidth: 30,
      maxWidth: 85,
      flex: 0,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellRenderer: (params: any) => (
        <div className="flex gap-x-1 pt-1 pb-1">
          <button
            onClick={() => {
              handleView(params.data);
            }}
            className="p-1 hover:bg-red-100 rounded"
            title="View User"
          >
            <img className="iconTd w-5 h-5" src={images.viewIcon} />
          </button>
          <button
            onClick={() => handleEdit(params.data)}
            className="p-1 hover:bg-gray-100 rounded"
            title="Edit User"
          >
            <img className="iconTd w-5 h-5" src={images.editIcon} alt="edit" />
          </button>
        </div>
      ),
      filter: false,
    },
  ]);

  const defaultColDef = useMemo(() => {
    return {
      editable: false,
      filter: false,
      tooltipComponentParams: {
        color: "#ececec",
        bgColor: "#1f2937",
        textColor: "#fff",
      },
    };
  }, []);
  const theme = useMemo(() => {
    return gridTheme;
  }, []);

  return (
    <>
      <div className="px-2 py-4 mt-14 ml-2 min-h-100vh">
        <div className="flex justify-between items-center">
          <h2 className="mb-3">User Management</h2>

          <button
            onClick={handleCreate}
            className="flex items-center justify-center font-semibold gap-2 bg-[#0fa9a6] text-white px-3 py-2 rounded-full"
          >
            <img src={images.createUserImg} width={20} alt="create-user" />
            CREATE USER
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-3 mb-4">
          {/* Search */}
          <div className="input-affix flex-1 min-w-[200px] max-w-xs mb-0">
            <i className="prefix-icon anticon anticon-search"></i>
            <input
              type="text"
              className="form-control"
              onChange={(e) => searchData(e.target.value)}
              placeholder="Search Here..."
            />
          </div>

          {/* Filters */}
          <span className="flex gap-3 flex-wrap">
            <Dropdown
              selectedValue={filterRegion}
              onChange={(value) => {
                setFilterRegion(value);
                setFilterPremise("");
              }}
              type="region"
            />
            <Dropdown
              selectedValue={filterPremise}
              onChange={(value) => setFilterPremise(value)}
              type="premises"
              selectedRegion={filterRegion}
            />
          </span>

          {/* Clear filters */}
          {(filterRegion || filterPremise) && (
            <button
              onClick={() => {
                setFilterRegion("");
                setFilterPremise("");
              }}
              className="text-sm text-[#0fa9a6] font-medium hover:underline whitespace-nowrap"
            >
              Clear filters
            </button>
          )}
        </div>

        <div className="tableBlock w-full ">
          <div className="card">
            {filteredRowData.length > 0 ? (
              <div className="ag-theme-alpine w-full min-h-[400px] border-0 rounded-lg shadow-lg">
                <AgGridReact
                  rowData={filteredRowData}
                  columnDefs={columnDefs}
                  theme={theme}
                  headerHeight={65}
                  rowHeight={50}
                  loading={isLoading}
                  defaultColDef={defaultColDef}
                  domLayout="autoHeight"
                />
              </div>
            ) : (
              <div className="flex flex-col justify-center items-center h-64">
                <p className="text-gray-500 text-lg mb-4">
                  {filterRegion || filterPremise
                    ? "No users match the selected filters"
                    : "No Users added yet"}
                </p>
                {(filterRegion || filterPremise) && (
                  <button
                    onClick={() => {
                      setFilterRegion("");
                      setFilterPremise("");
                    }}
                    className="text-sm text-[#0fa9a6] font-medium hover:underline"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        closeButton={false}
        modalClass={"bg-white w-full max-w-4xl rounded-xl shadow-lg relative"}
      >
        {
          <>
            <div className="pt-6">
              <h2 className="text-xl font-semibold text-gray-800 border-b px-6 pb-2 mb-4">
                {title}
              </h2>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleModalSubmit(form);
                }}
                // onSubmit={handleModalSubmit}
              >
                <div className="grid grid-cols-3 gap-4 px-6 pb-6">
                  {(
                    [
                      ["Employee ID", "employeeId"],
                      ["First Name", "firstName"],
                      ["Last Name", "lastName"],
                      ["Email", "email"],
                      ["Mobile", "mobile"],
                      ["Department", "department"],
                      ["Region", "region"],
                      ["Premise", "premise"],
                      ["Supervisor", "supervisor"],
                      // ["User Type", "userType"],
                      ["Role", "role"],
                      ["Password", "password"],
                    ] as [string, keyof userFormData][]
                  ).map(([label, name]) => {
                    const isSelect = name === "role" || name === "department";
                    // name === "region" ||
                    // name === "userType";
                    // name === "premise";

                    const isMultiSelect =
                      name === "region" || name === "premise";

                    return (
                      <div key={name}>
                        <label className="text-sm block mb-1">{label}</label>
                        {isSelect ? (
                          <select
                            name={name}
                            value={form[name]}
                            onChange={handleChange}
                            disabled={isView}
                            className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#375998]"
                          >
                            <option value="">Select {label}</option>

                            {name === "role" && rolesData.length > 0 ? (
                              rolesData.map((role: any) => (
                                <option key={role._id} value={role._id}>
                                  {role.roleName || role.userRole}
                                </option>
                              ))
                            ) : name === "role" ? (
                              <option disabled>No roles available</option>
                            ) : null}

                            {name === "department" && departments.length > 0 ? (
                              departments.map((dept: any) => (
                                <option key={dept._id} value={dept._id}>
                                  {dept.departmentName}
                                </option>
                              ))
                            ) : name === "department" ? (
                              <option disabled>No departments available</option>
                            ) : null}

                            {/* {name === "region" && regions.length > 0 ? (
                              regions.map((region: any) => (
                                <option key={region._id} value={region._id}>
                                  {region.regionName}
                                </option>
                              ))
                            ) : name === "region" ? (
                              <option disabled>No regions available</option>
                            ) : null} */}

                            {/* {name === "userType" ? (
                               validUserTypes.map(type => (
                                    <option key={type.value} value={type.value}>
                                        {type.label}
                                    </option>
                                ))
                            ) : null} */}

                            {/* {name === "premise" && allPremises.length > 0 && selectedRegion ? (
                              allPremises.filter((premise: any) => premise.region === selectedRegion)
                              .map((premise: any) => (
                                <option key={premise._id} value={premise._id}>
                                  {premise.premisesName}
                                </option>
                              ))
                            ) : name === "premise" ? (
                              <option disabled>{!selectedRegion ? "Select a region first" : "No premises available"}</option>
                            ) : null} */}
                          </select>
                        ) : isMultiSelect ? (
                          <MultiSelectDropdown
                            options={
                              name === "region"
                                ? regions.map((region: any) => ({
                                    label: region.regionName,
                                    value: region._id,
                                  }))
                                : name === "premise"
                                  ? allPremises
                                      .filter((prem: any) =>
                                        form["region"]?.length > 0
                                          ? form["region"].includes(prem.region)
                                          : false,
                                      )
                                      .map((premise: any) => ({
                                        label: premise.premisesName,
                                        value: premise._id,
                                      }))
                                  : []
                            }
                            selectedValues={
                              Array.isArray(form[name]) ? form[name] : []
                            }
                            disabled={isView}
                            onChange={(vals) => {
                              if (name === "region") {
                                const currentPremises = Array.isArray(
                                  form.premise,
                                )
                                  ? form.premise
                                  : [form.premise].filter(Boolean);
                                const cleanedPremises = currentPremises?.filter(
                                  (premiseId) => {
                                    const matchedPrem = allPremises.find(
                                      (p: any) => p.id === premiseId,
                                    );
                                    return (
                                      matchedPrem &&
                                      vals.includes(matchedPrem.region)
                                    );
                                  },
                                );

                                setForm({
                                  ...form,
                                  region: vals,
                                  premise: cleanedPremises,
                                });
                              } else {
                                setForm({
                                  ...form,
                                  [name]: Array.isArray(vals) ? vals : [vals],
                                });
                              }
                            }}
                            placeholder={`Select ${label?.toLowerCase()}...`}
                          />
                        ) : (
                          <input
                            type={name === "password" ? "password" : "text"}
                            name={name}
                            value={form[name]}
                            onChange={handleChange}
                            disabled={
                              name === "password" ? isEdit || isView : isView
                            }
                            placeholder={`Enter ${label}`}
                            className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#375998]"
                          />
                        )}
                        {errors[name] && (
                          <p className="text-sm text-red-500 mt-1 capitalize">
                            {errors[name]}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
                {error && (
                  <p className="text-sm text-red-500  pl-4 capitalize">
                    {error}
                  </p>
                )}

                <div className="flex justify-end gap-3 mt-4 p-2 bg-[#5A7192] w-full rounded-b-lg">
                  <button
                    onClick={() => {
                      setModalOpen(false);
                      setForm(emptyForm);
                    }}
                    type="button"
                    className="bg-[#8497B0]  text-white px-4 py-2 rounded-full hover:opacity-90"
                  >
                    CANCEL
                  </button>
                  {!isView && (
                    <button
                      // onClick={handleModalSubmit}
                      type="submit"
                      className="bg-[#00A8A8]  text-white px-4 py-2 rounded-full hover:opacity-90"
                    >
                      SUBMIT
                    </button>
                  )}
                </div>
              </form>
            </div>
          </>
        }
      </Modal>
    </>
  );
};

export default UserManagement;
